import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Select,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { FaPlus } from 'react-icons/fa';
import { RiArrowDownSLine } from 'react-icons/ri';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import CommonTable from 'components/commonTable/CommonTable';
import { useColorModeVariables } from 'components/colorModeVarialbels/ColorModeValues';
import LabelBadge from 'components/labels/LabelBadge';
import LabelScopeModal from 'components/labels/LabelScopeModal';
import useListState from 'hooks/useListState';
import useLabels from 'hooks/useLabels';
import useRegions from 'hooks/useRegions';
import { GLOBAL_LABEL_SCOPE_REGION_ID, sideBarRoutes } from 'utils/StaticData';

const columnHelper = createColumnHelper();

// Create and Edit are full pages (LabelAddEdit), matching the app's other
// AddEdit conventions. Change scope (Super Admin only) lives directly on the
// Scope cell - a small pill/chevron, same interaction as the edit page's
// Scope field - rather than a separate Actions column with one icon in it.
const LabelList = () => {
  const { tabletextColor } = useColorModeVariables();
  const { regions } = useRegions();
  const navigate = useNavigate();
  const { adminDetails } = useSelector((state) => state.authSlice);
  // Only Super Admin manages Global or another region's labels; Dealer/Seller
  // are confined to their own region and can only view Global ones (RAT-1699).
  const isSuperAdmin = adminDetails?.role === sideBarRoutes?.Admin;
  const ownRegionId = adminDetails?.region?.regionId;
  const {
    labels,
    total,
    limit,
    isFetching,
    isSaving,
    fetchLabels,
    changeScope,
  } = useLabels();

  const { params, setParam, setParams, debouncedParams, page, setPage } =
    useListState({
      defaults: {
        search: '',
        regionId: '',
        limit: 10,
        sortKey: '',
        sortBy: '',
      },
      debounceKeys: ['search'],
    });

  // Only `search` is debounced; `regionId` is a plain select, so it applies
  // immediately from `params` (useListState only echoes debounceKeys into
  // debouncedParams).
  const filters = {
    search: debouncedParams.search,
    regionId: params.regionId,
    page,
    limit: params.limit,
    sortKey: params.sortKey,
    sortBy: params.sortBy,
  };

  useEffect(() => {
    fetchLabels({
      search: debouncedParams.search,
      regionId: params.regionId,
      page,
      limit: params.limit,
      sortKey: params.sortKey,
      sortBy: params.sortBy,
    });
  }, [
    fetchLabels,
    debouncedParams.search,
    params.regionId,
    page,
    params.limit,
    params.sortKey,
    params.sortBy,
  ]);

  const scopeModal = useDisclosure();
  const scopeLabelRef = useRef(null);

  const openScope = (label) => {
    scopeLabelRef.current = label;
    scopeModal.onOpen();
  };

  const handleScopeConfirm = async (payload) => {
    const ok = await changeScope(scopeLabelRef.current.id, payload, filters);
    if (ok) scopeModal.onClose();
  };

  const cellText = (children) => (
    <Text
      color={tabletextColor}
      fontSize="sm"
      fontWeight="600"
      align="left"
      whiteSpace="nowrap"
    >
      {children}
    </Text>
  );

  const headerText = (children) => (
    <Text
      whiteSpace="nowrap"
      align="left"
      fontSize={{ sm: '10px', lg: '12px' }}
      color="gray.400"
      fontFamily="Montserrat"
    >
      {children}
    </Text>
  );

  const columns = [
    columnHelper.accessor((row) => row?.id, {
      id: 'id',
      header: () => headerText('ID'),
      cell: (info) => cellText(info.getValue()),
    }),
    columnHelper.accessor((row) => row, {
      id: 'label',
      header: () => headerText('Label'),
      cell: (info) => <LabelBadge label={info.getValue()} />,
    }),
    columnHelper.accessor((row) => row, {
      id: 'regionName',
      header: () => headerText('Scope'),
      cell: (info) => {
        const row = info.getValue();
        if (!isSuperAdmin) {
          return cellText(row?.regionName);
        }
        return (
          <Tooltip label="Change scope" fontSize="xs" openDelay={300}>
            <Button
              size="sm"
              variant="outline"
              borderColor="#d8dadc"
              background="#fff"
              fontWeight="600"
              color={tabletextColor}
              px="10px"
              h="auto"
              py="4px"
              rightIcon={
                <Icon as={RiArrowDownSLine} color="gray.500" boxSize="14px" />
              }
              onClick={() => openScope(row)}
            >
              {row?.regionName}
            </Button>
          </Tooltip>
        );
      },
    }),
    columnHelper.accessor((row) => row?.merchantCount, {
      id: 'merchantCount',
      header: () => headerText('Merchants'),
      cell: (info) => cellText(info.getValue()),
    }),
  ];

  return (
    <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
      {/* Filters - titled, matching the Orders/Stations list convention */}
      <Flex
        flexWrap="wrap"
        align="flex-end"
        mb={4}
        gap={{ base: '14px', md: '20px' }}
      >
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={params.search ? 'green.500' : 'gray.400'}
          >
            Search
          </Text>
          <CommonSearch
            search={params.search}
            setSearch={(val) => setParam('search', val)}
            setPage={setPage}
            placeholder="Label name or ID"
          />
        </Box>
        <Box width={{ base: '100%', md: 'auto' }}>
          <Text
            fontSize="xs"
            fontWeight="600"
            mb="1"
            color={params.regionId !== '' ? 'green.500' : 'gray.400'}
          >
            Scope
          </Text>
          <Select
            value={params.regionId}
            onChange={(e) => setParam('regionId', e.target.value)}
            width={{ base: '100%', md: '200px' }}
            background="#fff"
            borderRadius="10px"
            fontWeight="500"
          >
            <option value="">All</option>
            <option value={GLOBAL_LABEL_SCOPE_REGION_ID}>Global</option>
            {/* Dealer/Seller only ever see their own region + Global (the
                backend scopes list() the same way), so other regions would
                just filter to zero results - don't offer them. */}
            {regions
              .filter((r) => isSuperAdmin || r.id === ownRegionId)
              .map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
          </Select>
        </Box>
        <Box flex="1" />
        <Button
          bg="reviveOrange"
          color="primary"
          borderRadius="12px"
          _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
          onClick={() => navigate('/admin/labels/add')}
        >
          <Icon
            as={FaPlus}
            width="15px"
            height="15px"
            marginRight="5px"
            color="inherit"
          />
          Create Label
        </Button>
      </Flex>

      <CommonTable
        columns={columns}
        data={labels}
        isLoading={isFetching}
        isLink
        parentLink="/admin/labels"
        keyValue="id"
        rowclick={['regionName']}
        excludes={['merchantCount']}
        isSorting
        defaultFilter={{ sortKey: params.sortKey, sortBy: params.sortBy }}
        getfilter={({ sortKey, sortBy }) => setParams({ sortKey, sortBy })}
      />

      {total > limit && (
        <CommonPagination
          totalCount={total}
          activePage={page}
          pageCount={Math.ceil(total / (limit || 10))}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
      )}

      <LabelScopeModal
        isOpen={scopeModal.isOpen}
        onClose={scopeModal.onClose}
        label={scopeLabelRef.current}
        onConfirm={handleScopeConfirm}
        isSaving={isSaving}
      />
    </Box>
  );
};

export default LabelList;
